import React, { useContext, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/checkout.css";

const initialFormData = {
  email: "",
  phone: "",
  firstName: "",
  lastName: "",
  company: "",
  street: "",
  addressExtra: "",
  postalCode: "",
  city: "",
  country: "Deutschland",
  shippingMethod: "standard",
  paymentMethod: "invoice",
  cardName: "",
  cardNumber: "",
  cardExpiry: "",
  cardCvc: "",
  acceptTerms: false,
};

const shippingMethods = [
  {
    id: "standard",
    title: "Standardversand nach Hause",
    description: "Voraussichtlich in 2–4 Werktagen",
  },
  {
    id: "bundled",
    title: "Gebündelte Zustellung",
    description: "Ressourcenschonend in 3–5 Werktagen",
  },
];

const paymentMethods = [
  {
    id: "invoice",
    title: "Kauf auf Rechnung",
    description: "Bequem nach Erhalt der Bestellung bezahlen",
  },
  {
    id: "paypal",
    title: "PayPal",
    description: "Sichere Demo-Zahlung ohne externe Weiterleitung",
  },
  {
    id: "card",
    title: "Kredit- oder Debitkarte",
    description: "Geschützte Eingabe für die simulierte Kartenzahlung",
  },
];

const stripCardNumber = (value) => value.replace(/\D/g, "");

const isValidCardNumber = (value) => {
  const digits = stripCardNumber(value);
  if (digits.length < 13 || digits.length > 19) return false;

  let sum = 0;
  let doubleDigit = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);
    if (doubleDigit) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    doubleDigit = !doubleDigit;
  }
  return sum % 10 === 0;
};

const isValidExpiry = (value) => {
  const match = value.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
  if (!match) return false;
  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  return new Date(year, month, 1) > new Date();
};

const formatCardNumber = (value) =>
  stripCardNumber(value)
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, "$1 ");

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

const createOrderNumber = (brandName) => {
  const date = new Date();
  const datePart = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("");
  const brandPrefix = (brandName || "Shop")
    .replace(/[^a-z0-9]/gi, "")
    .slice(0, 2)
    .toUpperCase();
  const uniquePart = date.getTime().toString(36).slice(-6).toUpperCase();
  return `${brandPrefix}-${datePart}-${uniquePart}`;
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const fieldRefs = useRef({});

  const {
    brandName,
    locale = "de-DE",
    currency = "EUR",
    freeShippingThreshold = 99,
    shippingFee = 4.95,
  } = siteConfig;
  const money = new Intl.NumberFormat(locale, { style: "currency", currency });

  const cartLines = cartItems.reduce((lines, item) => {
    const product = productData.find((entry) => entry.id === item.id);
    const quantity = Number(item.quantity);
    const unitPrice = Number(product?.price);
    if (!product || !Number.isFinite(quantity) || quantity < 1 || !Number.isFinite(unitPrice)) {
      return lines;
    }
    return [
      ...lines,
      {
        item,
        product,
        quantity,
        unitPrice,
        lineTotal: unitPrice * quantity,
      },
    ];
  }, []);

  const subtotal = cartLines.reduce((total, line) => total + line.lineTotal, 0);
  const threshold = Number(freeShippingThreshold) || 0;
  const baseShippingFee = Number(shippingFee) || 0;
  const activeDiscountCode = location.state?.discountCode;
  const configuredDiscountCode = siteConfig.cartPromotion?.code;
  const discountRate = Number(siteConfig.cartPromotion?.discountRate) || 0;
  const hasDiscount =
    Boolean(configuredDiscountCode) && activeDiscountCode === configuredDiscountCode;
  const discount = hasDiscount ? subtotal * discountRate : 0;
  const merchandiseTotal = Math.max(0, subtotal - discount);
  const shipping = merchandiseTotal >= threshold ? 0 : baseShippingFee;
  const total = merchandiseTotal + shipping;
  const amountUntilFreeShipping = Math.max(0, threshold - merchandiseTotal);
  const hasItems = cartLines.length > 0;

  const setFieldRef = (name) => (element) => {
    if (element) fieldRefs.current[name] = element;
  };

  const errorFor = (name) => errors[name];

  const updateField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      const keysToClear =
        name === "paymentMethod"
          ? [name, "cardName", "cardNumber", "cardExpiry", "cardCvc"]
          : name === "country"
            ? [name, "postalCode"]
            : [name];
      if (!keysToClear.some((key) => current[key])) return current;
      const nextErrors = { ...current };
      keysToClear.forEach((key) => delete nextErrors[key]);
      return nextErrors;
    });
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    updateField(name, type === "checkbox" ? checked : value);
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^[+\d][\d\s()/.-]{5,}$/;
    const postalPatterns = {
      Deutschland: /^\d{5}$/,
      Österreich: /^\d{4}$/,
      Schweiz: /^\d{4}$/,
    };

    if (!formData.email.trim()) {
      nextErrors.email = "Bitte gib deine E-Mail-Adresse ein.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Bitte prüfe deine E-Mail-Adresse.";
    }
    if (formData.phone.trim() && !phonePattern.test(formData.phone.trim())) {
      nextErrors.phone = "Bitte gib eine gültige Telefonnummer ein.";
    }
    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Bitte gib deinen Vornamen ein.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Bitte gib deinen Nachnamen ein.";
    }
    if (!formData.street.trim()) {
      nextErrors.street = "Bitte gib Straße und Hausnummer ein.";
    }
    if (!formData.postalCode.trim()) {
      nextErrors.postalCode = "Bitte gib deine Postleitzahl ein.";
    } else if (!postalPatterns[formData.country]?.test(formData.postalCode.trim())) {
      nextErrors.postalCode = "Bitte prüfe die Postleitzahl für das gewählte Land.";
    }
    if (!formData.city.trim()) {
      nextErrors.city = "Bitte gib deinen Ort ein.";
    }
    if (!formData.country) {
      nextErrors.country = "Bitte wähle ein Lieferland.";
    }
    if (!formData.shippingMethod) {
      nextErrors.shippingMethod = "Bitte wähle eine Versandart.";
    }
    if (!formData.paymentMethod) {
      nextErrors.paymentMethod = "Bitte wähle eine Zahlungsart.";
    }

    if (formData.paymentMethod === "card") {
      if (formData.cardName.trim().length < 3) {
        nextErrors.cardName = "Bitte gib den Namen auf der Karte ein.";
      }
      if (!isValidCardNumber(formData.cardNumber)) {
        nextErrors.cardNumber = "Bitte gib eine gültige Kartennummer ein.";
      }
      if (!isValidExpiry(formData.cardExpiry)) {
        nextErrors.cardExpiry = "Bitte gib ein gültiges Ablaufdatum ein.";
      }
      if (!/^\d{3,4}$/.test(formData.cardCvc)) {
        nextErrors.cardCvc = "Bitte gib den 3- oder 4-stelligen Prüfcode ein.";
      }
    }

    if (!formData.acceptTerms) {
      nextErrors.acceptTerms = "Bitte bestätige AGB und Datenschutzhinweise.";
    }
    return nextErrors;
  };

  const markImageAsFailed = (source) => {
    setFailedImages((current) => {
      const next = new Set(current);
      next.add(source);
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting || !hasItems) return;

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstError = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => fieldRefs.current[firstError]?.focus());
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    const estimatedDelivery =
      formData.shippingMethod === "bundled"
        ? "in 3–5 Werktagen"
        : "in 2–4 Werktagen";
    const orderItems = cartLines.map(({ item, product, quantity, unitPrice, lineTotal }) => ({
      id: product.id,
      sku: product.sku,
      name: product.name,
      image: product.image,
      size: item.size,
      color: item.color,
      quantity,
      price: unitPrice,
      unitPrice,
      lineTotal,
    }));
    const order = {
      orderNumber: createOrderNumber(brandName),
      total,
      subtotal,
      merchandiseTotal,
      discount,
      discountCode: hasDiscount ? configuredDiscountCode : null,
      shipping,
      email: formData.email.trim(),
      items: orderItems,
      currency,
      estimatedDelivery,
      paymentMethod: formData.paymentMethod,
      shippingMethod: formData.shippingMethod,
      shippingAddress: {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        company: formData.company.trim(),
        street: formData.street.trim(),
        addressExtra: formData.addressExtra.trim(),
        postalCode: formData.postalCode.trim(),
        city: formData.city.trim(),
        country: formData.country,
      },
    };

    clearCart();
    navigate("/paysuccess", {
      replace: true,
      state: { order },
    });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  if (!hasItems) {
    return (
      <main className="checkout-page">
        <section className="checkout-page__empty" aria-labelledby="empty-checkout-title">
          <span className="checkout-page__empty-mark" aria-hidden="true">○</span>
          <p className="checkout-page__eyebrow">Dein Warenkorb wartet</p>
          <h1 id="empty-checkout-title">Noch nichts zum Bestellen da.</h1>
          <p>
            Entdecke natürliche Lieblingsstücke und lege deine Auswahl zuerst in
            den Warenkorb. Danach kannst du hier sicher bestellen.
          </p>
          <Link className="checkout-page__primary-link" to="/shop">
            Kollektion entdecken
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-page__hero">
        <nav className="checkout-page__breadcrumb" aria-label="Brotkrümelnavigation">
          <Link to="/cartpage">Warenkorb</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Kasse</span>
        </nav>
        <div className="checkout-page__hero-grid">
          <div>
            <p className="checkout-page__eyebrow">Sicher bestellen</p>
            <h1>Fast bei dir.</h1>
          </div>
          <p>
            Prüfe deine Angaben und schließe deine Bestellung in wenigen
            übersichtlichen Schritten ab.
          </p>
        </div>
      </header>

      <div className="checkout-page__layout">
        <section className="checkout-page__form-column" aria-labelledby="checkout-form-title">
          <h2 className="checkout-page__visually-hidden" id="checkout-form-title">
            Bestelldaten
          </h2>
          <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting}>
            {Object.keys(errors).length > 0 && (
              <div className="checkout-page__error-summary" role="alert">
                <strong>Bitte prüfe deine Angaben.</strong>
                <span>Die markierten Felder sind noch nicht vollständig.</span>
              </div>
            )}

            <section className="checkout-page__step" aria-labelledby="contact-step-title">
              <div className="checkout-page__step-heading">
                <span aria-hidden="true">01</span>
                <div>
                  <p className="checkout-page__eyebrow">Kontakt</p>
                  <h2 id="contact-step-title">Wie erreichen wir dich?</h2>
                </div>
              </div>
              <div className="checkout-page__field-grid">
                <div className="checkout-page__field">
                  <label htmlFor="checkout-email">E-Mail-Adresse *</label>
                  <input
                    id="checkout-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={formData.email}
                    onChange={handleChange}
                    ref={setFieldRef("email")}
                    aria-invalid={Boolean(errorFor("email"))}
                    aria-describedby={errorFor("email") ? "checkout-email-error" : undefined}
                  />
                  {errorFor("email") && (
                    <p className="checkout-page__error" id="checkout-email-error">
                      {errorFor("email")}
                    </p>
                  )}
                </div>
                <div className="checkout-page__field">
                  <label htmlFor="checkout-phone">Telefon (optional)</label>
                  <input
                    id="checkout-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    ref={setFieldRef("phone")}
                    aria-invalid={Boolean(errorFor("phone"))}
                    aria-describedby={errorFor("phone") ? "checkout-phone-error" : undefined}
                  />
                  {errorFor("phone") && (
                    <p className="checkout-page__error" id="checkout-phone-error">
                      {errorFor("phone")}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="checkout-page__step" aria-labelledby="address-step-title">
              <div className="checkout-page__step-heading">
                <span aria-hidden="true">02</span>
                <div>
                  <p className="checkout-page__eyebrow">Lieferadresse</p>
                  <h2 id="address-step-title">Wohin dürfen wir liefern?</h2>
                </div>
              </div>
              <div className="checkout-page__field-grid">
                <div className="checkout-page__field checkout-page__field--full">
                  <label htmlFor="checkout-country">Land/Region *</label>
                  <select
                    id="checkout-country"
                    name="country"
                    autoComplete="country-name"
                    value={formData.country}
                    onChange={handleChange}
                    ref={setFieldRef("country")}
                    aria-invalid={Boolean(errorFor("country"))}
                    aria-describedby={errorFor("country") ? "checkout-country-error" : undefined}
                  >
                    <option value="Deutschland">Deutschland</option>
                    <option value="Österreich">Österreich</option>
                    <option value="Schweiz">Schweiz</option>
                  </select>
                  {errorFor("country") && (
                    <p className="checkout-page__error" id="checkout-country-error">
                      {errorFor("country")}
                    </p>
                  )}
                </div>

                <div className="checkout-page__field">
                  <label htmlFor="checkout-first-name">Vorname *</label>
                  <input
                    id="checkout-first-name"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    ref={setFieldRef("firstName")}
                    aria-invalid={Boolean(errorFor("firstName"))}
                    aria-describedby={errorFor("firstName") ? "checkout-first-name-error" : undefined}
                  />
                  {errorFor("firstName") && (
                    <p className="checkout-page__error" id="checkout-first-name-error">
                      {errorFor("firstName")}
                    </p>
                  )}
                </div>
                <div className="checkout-page__field">
                  <label htmlFor="checkout-last-name">Nachname *</label>
                  <input
                    id="checkout-last-name"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    ref={setFieldRef("lastName")}
                    aria-invalid={Boolean(errorFor("lastName"))}
                    aria-describedby={errorFor("lastName") ? "checkout-last-name-error" : undefined}
                  />
                  {errorFor("lastName") && (
                    <p className="checkout-page__error" id="checkout-last-name-error">
                      {errorFor("lastName")}
                    </p>
                  )}
                </div>

                <div className="checkout-page__field checkout-page__field--full">
                  <label htmlFor="checkout-company">Firma (optional)</label>
                  <input
                    id="checkout-company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="checkout-page__field checkout-page__field--full">
                  <label htmlFor="checkout-street">Straße und Hausnummer *</label>
                  <input
                    id="checkout-street"
                    name="street"
                    type="text"
                    autoComplete="address-line1"
                    value={formData.street}
                    onChange={handleChange}
                    ref={setFieldRef("street")}
                    aria-invalid={Boolean(errorFor("street"))}
                    aria-describedby={errorFor("street") ? "checkout-street-error" : undefined}
                  />
                  {errorFor("street") && (
                    <p className="checkout-page__error" id="checkout-street-error">
                      {errorFor("street")}
                    </p>
                  )}
                </div>
                <div className="checkout-page__field checkout-page__field--full">
                  <label htmlFor="checkout-address-extra">Adresszusatz (optional)</label>
                  <input
                    id="checkout-address-extra"
                    name="addressExtra"
                    type="text"
                    autoComplete="address-line2"
                    value={formData.addressExtra}
                    onChange={handleChange}
                  />
                </div>
                <div className="checkout-page__field">
                  <label htmlFor="checkout-postal-code">Postleitzahl *</label>
                  <input
                    id="checkout-postal-code"
                    name="postalCode"
                    type="text"
                    autoComplete="postal-code"
                    inputMode="numeric"
                    value={formData.postalCode}
                    onChange={handleChange}
                    ref={setFieldRef("postalCode")}
                    aria-invalid={Boolean(errorFor("postalCode"))}
                    aria-describedby={errorFor("postalCode") ? "checkout-postal-code-error" : undefined}
                  />
                  {errorFor("postalCode") && (
                    <p className="checkout-page__error" id="checkout-postal-code-error">
                      {errorFor("postalCode")}
                    </p>
                  )}
                </div>
                <div className="checkout-page__field">
                  <label htmlFor="checkout-city">Ort *</label>
                  <input
                    id="checkout-city"
                    name="city"
                    type="text"
                    autoComplete="address-level2"
                    value={formData.city}
                    onChange={handleChange}
                    ref={setFieldRef("city")}
                    aria-invalid={Boolean(errorFor("city"))}
                    aria-describedby={errorFor("city") ? "checkout-city-error" : undefined}
                  />
                  {errorFor("city") && (
                    <p className="checkout-page__error" id="checkout-city-error">
                      {errorFor("city")}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="checkout-page__step" aria-labelledby="shipping-step-title">
              <div className="checkout-page__step-heading">
                <span aria-hidden="true">03</span>
                <div>
                  <p className="checkout-page__eyebrow">Versandart</p>
                  <h2 id="shipping-step-title">Wie soll dein Paket kommen?</h2>
                </div>
              </div>
              <fieldset
                className="checkout-page__choice-group"
                aria-invalid={Boolean(errorFor("shippingMethod"))}
                aria-describedby={
                  errorFor("shippingMethod") ? "checkout-shipping-error" : undefined
                }
              >
                <legend className="checkout-page__visually-hidden">Versandart wählen</legend>
                {shippingMethods.map((method, index) => (
                  <label
                    className={`checkout-page__choice${
                      formData.shippingMethod === method.id ? " is-selected" : ""
                    }`}
                    key={method.id}
                  >
                    <input
                      type="radio"
                      name="shippingMethod"
                      value={method.id}
                      checked={formData.shippingMethod === method.id}
                      onChange={handleChange}
                      ref={index === 0 ? setFieldRef("shippingMethod") : undefined}
                    />
                    <span className="checkout-page__radio-mark" aria-hidden="true" />
                    <span>
                      <strong>{method.title}</strong>
                      <small>{method.description}</small>
                    </span>
                    <b>{shipping === 0 ? "Kostenfrei" : money.format(shipping)}</b>
                  </label>
                ))}
              </fieldset>
              {errorFor("shippingMethod") && (
                <p className="checkout-page__error" id="checkout-shipping-error">
                  {errorFor("shippingMethod")}
                </p>
              )}
            </section>

            <section className="checkout-page__step" aria-labelledby="payment-step-title">
              <div className="checkout-page__step-heading">
                <span aria-hidden="true">04</span>
                <div>
                  <p className="checkout-page__eyebrow">Zahlung</p>
                  <h2 id="payment-step-title">Wie möchtest du bezahlen?</h2>
                </div>
              </div>
              <fieldset
                className="checkout-page__choice-group checkout-page__choice-group--payment"
                aria-invalid={Boolean(errorFor("paymentMethod"))}
                aria-describedby={
                  errorFor("paymentMethod") ? "checkout-payment-error" : undefined
                }
              >
                <legend className="checkout-page__visually-hidden">Zahlungsart wählen</legend>
                {paymentMethods.map((method, index) => (
                  <label
                    className={`checkout-page__choice${
                      formData.paymentMethod === method.id ? " is-selected" : ""
                    }`}
                    key={method.id}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={formData.paymentMethod === method.id}
                      onChange={handleChange}
                      ref={index === 0 ? setFieldRef("paymentMethod") : undefined}
                    />
                    <span className="checkout-page__radio-mark" aria-hidden="true" />
                    <span>
                      <strong>{method.title}</strong>
                      <small>{method.description}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              {errorFor("paymentMethod") && (
                <p className="checkout-page__error" id="checkout-payment-error">
                  {errorFor("paymentMethod")}
                </p>
              )}

              {formData.paymentMethod === "invoice" && (
                <div className="checkout-page__payment-note">
                  <span aria-hidden="true">✓</span>
                  <p>
                    Die Zahlungsinformationen erhältst du mit deiner Bestellung.
                    Bitte überweise erst nach Erhalt der Rechnung.
                  </p>
                </div>
              )}
              {formData.paymentMethod === "paypal" && (
                <div className="checkout-page__payment-note">
                  <span aria-hidden="true">P</span>
                  <p>
                    Dies ist eine Frontend-Simulation. Es findet keine externe
                    Weiterleitung und keine echte Belastung statt.
                  </p>
                </div>
              )}
              {formData.paymentMethod === "card" && (
                <div className="checkout-page__card-panel">
                  <p>
                    Demo-Kartenzahlung – Kartendaten werden weder gespeichert noch
                    an einen Zahlungsdienst übertragen.
                  </p>
                  <div className="checkout-page__field-grid">
                    <div className="checkout-page__field checkout-page__field--full">
                      <label htmlFor="checkout-card-name">Name auf der Karte *</label>
                      <input
                        id="checkout-card-name"
                        name="cardName"
                        type="text"
                        autoComplete="cc-name"
                        value={formData.cardName}
                        onChange={handleChange}
                        ref={setFieldRef("cardName")}
                        aria-invalid={Boolean(errorFor("cardName"))}
                        aria-describedby={errorFor("cardName") ? "checkout-card-name-error" : undefined}
                      />
                      {errorFor("cardName") && (
                        <p className="checkout-page__error" id="checkout-card-name-error">
                          {errorFor("cardName")}
                        </p>
                      )}
                    </div>
                    <div className="checkout-page__field checkout-page__field--full">
                      <label htmlFor="checkout-card-number">Kartennummer *</label>
                      <input
                        id="checkout-card-number"
                        name="cardNumber"
                        type="text"
                        autoComplete="cc-number"
                        inputMode="numeric"
                        placeholder="1234 5678 9012 3456"
                        value={formData.cardNumber}
                        onChange={(event) => updateField("cardNumber", formatCardNumber(event.target.value))}
                        ref={setFieldRef("cardNumber")}
                        aria-invalid={Boolean(errorFor("cardNumber"))}
                        aria-describedby={errorFor("cardNumber") ? "checkout-card-number-error" : undefined}
                      />
                      {errorFor("cardNumber") && (
                        <p className="checkout-page__error" id="checkout-card-number-error">
                          {errorFor("cardNumber")}
                        </p>
                      )}
                    </div>
                    <div className="checkout-page__field">
                      <label htmlFor="checkout-card-expiry">Gültig bis *</label>
                      <input
                        id="checkout-card-expiry"
                        name="cardExpiry"
                        type="text"
                        autoComplete="cc-exp"
                        inputMode="numeric"
                        placeholder="MM/JJ"
                        value={formData.cardExpiry}
                        onChange={(event) => updateField("cardExpiry", formatExpiry(event.target.value))}
                        ref={setFieldRef("cardExpiry")}
                        aria-invalid={Boolean(errorFor("cardExpiry"))}
                        aria-describedby={errorFor("cardExpiry") ? "checkout-card-expiry-error" : undefined}
                      />
                      {errorFor("cardExpiry") && (
                        <p className="checkout-page__error" id="checkout-card-expiry-error">
                          {errorFor("cardExpiry")}
                        </p>
                      )}
                    </div>
                    <div className="checkout-page__field">
                      <label htmlFor="checkout-card-cvc">Prüfcode *</label>
                      <input
                        id="checkout-card-cvc"
                        name="cardCvc"
                        type="text"
                        autoComplete="cc-csc"
                        inputMode="numeric"
                        maxLength="4"
                        value={formData.cardCvc}
                        onChange={(event) =>
                          updateField("cardCvc", event.target.value.replace(/\D/g, "").slice(0, 4))
                        }
                        ref={setFieldRef("cardCvc")}
                        aria-invalid={Boolean(errorFor("cardCvc"))}
                        aria-describedby={errorFor("cardCvc") ? "checkout-card-cvc-error" : undefined}
                      />
                      {errorFor("cardCvc") && (
                        <p className="checkout-page__error" id="checkout-card-cvc-error">
                          {errorFor("cardCvc")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="checkout-page__consent">
              <input
                id="checkout-terms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleChange}
                ref={setFieldRef("acceptTerms")}
                aria-invalid={Boolean(errorFor("acceptTerms"))}
                aria-describedby={errorFor("acceptTerms") ? "checkout-terms-error" : undefined}
              />
              <label htmlFor="checkout-terms">
                Ich akzeptiere die <Link to="/terms">AGB</Link> und habe die{" "}
                <Link to="/privacy">Datenschutzhinweise</Link> gelesen. *
              </label>
            </div>
            {errorFor("acceptTerms") && (
              <p className="checkout-page__error" id="checkout-terms-error">
                {errorFor("acceptTerms")}
              </p>
            )}

            <button
              className="checkout-page__submit"
              type="submit"
              disabled={isSubmitting || !hasItems}
            >
              {isSubmitting
                ? "Bestellung wird abgeschlossen …"
                : formData.paymentMethod === "card"
                  ? `Jetzt ${money.format(total)} bezahlen`
                  : `Zahlungspflichtig für ${money.format(total)} bestellen`}
            </button>
            <p className="checkout-page__secure-note">
              <span aria-hidden="true">◇</span> Deine Angaben werden nur für die
              Bestellabwicklung verwendet.
            </p>
          </form>
        </section>

        <aside className="checkout-page__summary" aria-labelledby="order-summary-title">
          <button
            className="checkout-page__summary-toggle"
            type="button"
            aria-expanded={summaryOpen}
            aria-controls="checkout-summary-content"
            onClick={() => setSummaryOpen((current) => !current)}
          >
            <span>Bestellübersicht</span>
            <strong>{money.format(total)}</strong>
            <span aria-hidden="true">{summaryOpen ? "−" : "+"}</span>
          </button>

          <div
            className={`checkout-page__summary-content${summaryOpen ? " is-open" : ""}`}
            id="checkout-summary-content"
          >
            <div className="checkout-page__summary-heading">
              <p className="checkout-page__eyebrow">Deine Auswahl</p>
              <h2 id="order-summary-title">Bestellübersicht</h2>
              <Link to="/cartpage">Warenkorb bearbeiten</Link>
            </div>

            <ul className="checkout-page__items">
              {cartLines.map(({ item, product, quantity, unitPrice, lineTotal }) => (
                <li key={`${product.id}-${item.size}-${item.color}`}>
                  <Link
                    className="checkout-page__item-image"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`${product.name} ansehen`}
                  >
                    {failedImages.has(product.image) ? (
                      <span className="checkout-page__image-fallback" aria-hidden="true">◌</span>
                    ) : (
                      <img
                        src={product.image}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={() => markImageAsFailed(product.image)}
                      />
                    )}
                    <span className="checkout-page__item-quantity">{quantity}</span>
                  </Link>
                  <div className="checkout-page__item-copy">
                    <h3>
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                    </h3>
                    <p>{item.color} · Größe {item.size}</p>
                    <small>{quantity} × {money.format(unitPrice)}</small>
                  </div>
                  <strong>{money.format(lineTotal)}</strong>
                </li>
              ))}
            </ul>

            {amountUntilFreeShipping > 0 ? (
              <div className="checkout-page__shipping-progress">
                <p>Noch {money.format(amountUntilFreeShipping)} bis zum kostenfreien Versand.</p>
                <span aria-hidden="true">
                  <i
                    style={{
                      width: `${Math.min(
                        100,
                        threshold ? (merchandiseTotal / threshold) * 100 : 100
                      )}%`,
                    }}
                  />
                </span>
              </div>
            ) : (
              <p className="checkout-page__free-shipping">✓ Kostenloser Standardversand erreicht</p>
            )}

            <dl className="checkout-page__totals">
              <div>
                <dt>Zwischensumme</dt>
                <dd>{money.format(subtotal)}</dd>
              </div>
              {hasDiscount && (
                <div className="checkout-page__discount">
                  <dt>Rabatt {configuredDiscountCode}</dt>
                  <dd>− {money.format(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Versand</dt>
                <dd>{shipping === 0 ? "Kostenfrei" : money.format(shipping)}</dd>
              </div>
              <div className="checkout-page__grand-total">
                <dt>
                  Gesamt <small>inkl. MwSt.</small>
                </dt>
                <dd>{money.format(total)}</dd>
              </div>
            </dl>

            <div className="checkout-page__summary-trust">
              <span>30 Tage Rückgabe</span>
              <span>Sichere Bestellung</span>
              <span>Faire Naturmode</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
